// frontend/src/components/meetings/MeetingDetails.jsx
import React, { useState, useEffect } from "react";
import { X, Download, Copy, Check, Users, Clock, Calendar } from "lucide-react";
import api from "../../services/api";

const MeetingDetails = ({ meeting, onClose }) => {
  const [activeTab, setActiveTab] = useState("transcript");
  const [copied, setCopied] = useState(false);
  const [fullMeeting, setFullMeeting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMeetingDetails();
  }, [meeting.id]);

  const fetchMeetingDetails = async () => {
    try {
      setLoading(true);
      const response = await api.getMeetingDetails(meeting.id);
      setFullMeeting(response.meeting);
    } catch (error) {
      console.error("Failed to fetch meeting details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTranscript = () => {
    if (!fullMeeting?.transcripts) return;

    const text = fullMeeting.transcripts
      .map((t) => `[${formatTime(t.startTime)}] ${t.speakerName}: ${t.text}`)
      .join("\n\n");

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0C0E12] rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-gray-700">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {meeting.title || "Meeting Details"}
            </h2>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {new Date(meeting.scheduledAt).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={14} />
                {meeting.duration
                  ? `${Math.floor(meeting.duration / 60)} min`
                  : "N/A"}
              </span>
              <span className="flex items-center gap-1">
                <img
                  src={`/assets/${meeting.platform}.png`}
                  alt={meeting.platform}
                  className="w-4 h-4"
                />
                {meeting.platform}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          {["transcript", "insights", "recording"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 capitalize transition-colors ${
                activeTab === tab
                  ? "text-white border-b-2 border-purple-500"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 200px)" }}
        >
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : (
            <>
              {/* Transcript Tab */}
              {activeTab === "transcript" && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-white">
                      Meeting Transcript
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCopyTranscript}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors flex items-center gap-2"
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        {copied ? "Copied!" : "Copy"}
                      </button>
                      <button
                        onClick={() => api.downloadTranscript(meeting.id)}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors flex items-center gap-2"
                      >
                        <Download size={14} />
                        Download
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {fullMeeting?.transcripts?.map((transcript, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="text-xs text-gray-500 min-w-[60px]">
                          {formatTime(transcript.startTime)}
                        </div>
                        <div className="flex-1">
                          <span className="text-purple-400 font-medium">
                            {transcript.speakerName}:
                          </span>
                          <p className="text-gray-300 mt-1">
                            {transcript.text}
                          </p>
                        </div>
                      </div>
                    )) || (
                      <p className="text-gray-400">No transcript available</p>
                    )}
                  </div>
                </div>
              )}

              {/* Insights Tab */}
              {activeTab === "insights" && (
                <div className="space-y-6">
                  {/* Summary */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Summary
                    </h3>
                    <p className="text-gray-300">
                      {fullMeeting?.insights?.summary || "No summary available"}
                    </p>
                  </div>

                  {/* Action Items */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Action Items
                    </h3>
                    {fullMeeting?.insights?.actionItems?.length > 0 ? (
                      <ul className="space-y-2">
                        {fullMeeting.insights.actionItems.map((item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-purple-400 mt-1">•</span>
                            <div>
                              <p className="text-gray-300">{item.task}</p>
                              {item.assignee && (
                                <span className="text-xs text-gray-500">
                                  Assigned to: {item.assignee}
                                </span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-400">No action items found</p>
                    )}
                  </div>

                  {/* Tags */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Tags
                    </h3>
                    <div className="flex gap-2 flex-wrap">
                      {fullMeeting?.insights?.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-purple-600/20 border border-purple-600 rounded-lg text-sm text-purple-300"
                        >
                          {tag}
                        </span>
                      )) || <span className="text-gray-400">No tags</span>}
                    </div>
                  </div>

                  {/* Key Topics */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Key Topics
                    </h3>
                    {fullMeeting?.insights?.keyTopics?.length > 0 ? (
                      <div className="space-y-2">
                        {fullMeeting.insights.keyTopics.map((topic, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center"
                          >
                            <span className="text-gray-300">{topic.topic}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-700 rounded-full h-2">
                                <div
                                  className="bg-purple-500 h-2 rounded-full"
                                  style={{ width: `${topic.importance * 20}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500">
                                {topic.importance}/5
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400">No topics identified</p>
                    )}
                  </div>
                </div>
              )}

              {/* Recording Tab */}
              {activeTab === "recording" && (
                <div>
                  {meeting.recordingUrl ? (
                    <div>
                      <video
                        controls
                        className="w-full rounded-lg"
                        src={meeting.recordingUrl}
                      >
                        Your browser does not support the video tag.
                      </video>
                      <div className="mt-4 flex justify-center">
                        <a
                          href={meeting.recordingUrl}
                          download
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <Download size={16} />
                          Download Recording
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      No recording available
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingDetails;
